import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, Link2, Mail, Phone } from 'lucide-react';
import moment from 'moment';

import {
  useCancelOrgInviteMutation,
  useInviteNonFFUserMutation,
  useOrgInviteLinkQuery,
  useOrgInvitesQuery,
  useOrgRequestsQuery,
  useOrgRoleCatalogQuery,
  useUpdateInviteRoleMutation,
} from '@/client/api/admin/org-members';
import { formatPhoneForDisplay, normalizePhoneList } from '@/client/utils/phone';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

const InvitesTab = ({ oid }: { oid: number }) => {
  const requestsQuery = useOrgRequestsQuery(oid);
  const invitesQuery = useOrgInvitesQuery(oid);
  const inviteLinkQuery = useOrgInviteLinkQuery(oid);
  const roleCatalog = useOrgRoleCatalogQuery({ permissions: 0 });

  const inviteMutation = useInviteNonFFUserMutation();
  const cancelMutation = useCancelOrgInviteMutation();
  const updateRoleMutation = useUpdateInviteRoleMutation();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<{ ident: string; kind: string } | null>(
    null
  );

  const requests = requestsQuery.data?.requests ?? [];
  const invites = invitesQuery.data?.invites ?? [];

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!role) {
      setFormError('Pick a role.');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setFormError('Provide an email address, a phone number, or both.');
      return;
    }

    const { valid: phones, invalid } = normalizePhoneList(phone);
    if (invalid.length > 0) {
      setFormError(
        `Not a valid phone number: ${invalid.join(', ')}. Use a local (0244…) or international (+233…) format.`
      );
      return;
    }
    setFormError(null);

    const toastId = toast.loading('Sending invite…');
    try {
      await inviteMutation.mutateAsync({
        oid,
        role,
        email: email.trim() || undefined,
        phone: phones.join(',') || undefined,
      });
      toast.success('Invite sent', { id: toastId, richColors: true });
      setEmail('');
      setPhone('');
    } catch (error: any) {
      toast.error('Failed to send invite', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    const toastId = toast.loading('Cancelling…');
    try {
      await cancelMutation.mutateAsync({ oid, ident: cancelTarget.ident });
      toast.success('Cancelled', {
        id: toastId,
        description: `${cancelTarget.kind} for ${cancelTarget.ident} was cancelled.`,
        richColors: true,
      });
    } catch (error: any) {
      toast.error('Failed to cancel', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    } finally {
      setCancelTarget(null);
    }
  };

  const handleRoleChange = async (ident: string, nextRole: string) => {
    const toastId = toast.loading('Updating invite role…');
    try {
      await updateRoleMutation.mutateAsync({ oid, ident, role: nextRole });
      toast.success('Invite role updated', { id: toastId, richColors: true });
    } catch (error: any) {
      toast.error('Failed to update invite role', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const copyLink = async () => {
    const link = inviteLinkQuery.data?.invite_link;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy the link — copy it manually.');
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">
            Join requests from FeelyFeely users
          </h2>
          <p className="mt-1 text-sm text-gray-500">Existing users who asked to join.</p>
          <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Requests can only be cancelled through the invite-cancel route, which is keyed by
            email, phone, or invite hash — and the requests payload carries none of those, only a
            uid. There is no identifier to send, so cancelling from here is unavailable rather
            than firing a call that would not match anything.
          </p>

          <div className="mt-4 divide-y rounded-md border border-gray-200">
            {requestsQuery.isLoading && <p className="p-4 text-sm text-gray-500">Loading…</p>}
            {!requestsQuery.isLoading && requests.length === 0 && (
              <p className="p-4 text-sm text-gray-500">No pending requests.</p>
            )}
            {requests.map(request => (
              <div key={request.ouid} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">uid {request.uid}</p>
                  <p className="text-xs text-gray-500">
                    Requested{' '}
                    {request.date_added ? moment(request.date_added).format('YYYY-MM-DD') : '—'}
                  </p>
                </div>
                <Button variant="outline" size="sm" disabled title="No identifier available">
                  Cancel request
                </Button>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Pending invites</h2>
          <p className="mt-1 text-sm text-gray-500">
            Invitations sent to people who do not yet have a FeelyFeely account.
          </p>

          <div className="mt-4 divide-y rounded-md border border-gray-200">
            {invitesQuery.isLoading && <p className="p-4 text-sm text-gray-500">Loading…</p>}
            {!invitesQuery.isLoading && invites.length === 0 && (
              <p className="p-4 text-sm text-gray-500">No pending invites.</p>
            )}
            {invites.map(invite => {
              const ident = invite.email || invite.phone || invite.hash;
              return (
                <div
                  key={invite.hash}
                  className="flex flex-wrap items-center justify-between gap-3 p-3"
                >
                  <div>
                    <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      {invite.email ? (
                        <>
                          <Mail className="h-3 w-3 text-gray-400" />
                          {invite.email}
                        </>
                      ) : (
                        <>
                          <Phone className="h-3 w-3 text-gray-400" />
                          {invite.phone}
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      Sent{' '}
                      {invite.date_added ? moment(invite.date_added).format('YYYY-MM-DD') : '—'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                      value={invite.role}
                      disabled={updateRoleMutation.isPending}
                      onChange={e => void handleRoleChange(ident, e.target.value)}
                    >
                      {(roleCatalog.data ?? []).map(item => (
                        <option key={item.ourid} value={item.role}>
                          {item.role_friendly || item.role}
                        </option>
                      ))}
                      {!(roleCatalog.data ?? []).some(item => item.role === invite.role) && (
                        <option value={invite.role}>{invite.role}</option>
                      )}
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                      onClick={() => setCancelTarget({ ident, kind: 'Invite' })}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="space-y-6">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Invite someone new</h2>
          <form onSubmit={handleInvite} className="mt-4 space-y-4">
            <div className="space-y-1">
              <Label htmlFor="invite-role">
                Organization role <span className="text-red-500">*</span>
              </Label>
              <select
                id="invite-role"
                className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
                value={role}
                onChange={e => setRole(e.target.value)}
              >
                <option value="">Select a role…</option>
                {(roleCatalog.data ?? []).map(item => (
                  <option key={item.ourid} value={item.role}>
                    {item.role_friendly || item.role}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                value={email}
                placeholder="name@example.com (comma-separated for several)"
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="invite-phone">Phone</Label>
              <Input
                id="invite-phone"
                type="tel"
                inputMode="tel"
                value={phone}
                placeholder="0244… or +233… (comma-separated for several)"
                onChange={e => setPhone(e.target.value)}
                onBlur={e => {
                  const { valid, invalid } = normalizePhoneList(e.target.value);
                  if (invalid.length === 0 && valid.length > 0) {
                    setPhone(valid.map(v => formatPhoneForDisplay(v)).join(', '));
                  }
                }}
              />
            </div>

            <p className="text-xs text-gray-500">
              A role plus at least one of email or phone is required. Both may be given.
            </p>

            {formError && <p className="text-xs text-red-600">{formError}</p>}

            <Button type="submit" className="w-full" disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </form>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Link2 className="h-4 w-4 text-gray-400" />
            Invite link
          </h2>
          {inviteLinkQuery.isLoading ? (
            <Skeleton className="mt-4 h-9 w-full" />
          ) : inviteLinkQuery.data?.invite_link ? (
            <>
              <p className="mt-3 break-all rounded-md bg-gray-50 p-3 text-xs text-gray-700">
                {inviteLinkQuery.data.invite_link}
              </p>
              <Button variant="outline" className="mt-3 w-full" onClick={() => void copyLink()}>
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy link
                  </>
                )}
              </Button>
            </>
          ) : (
            <p className="mt-3 text-sm text-gray-500">No invite link is available.</p>
          )}
        </section>
      </div>

      <AlertDialog
        open={Boolean(cancelTarget)}
        onOpenChange={next => {
          if (!next) setCancelTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this {cancelTarget?.kind.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget?.ident} will no longer be able to join through it. You can send a new
              one afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelMutation.isPending}
              onClick={e => {
                e.preventDefault();
                void handleCancel();
              }}
            >
              {cancelMutation.isPending ? 'Cancelling…' : 'Cancel it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvitesTab;
